import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FileText, Plus, CheckCircle, Edit3, Trash2, Save, Loader2 } from 'lucide-react';
import { CJMSClient } from '../../api/cjmsApi';
import { feedbackBus } from '../../services/feedbackBus';

interface SOPDocumenterProps {
    client: CJMSClient;
}

interface SOPEntry {
    id: string;
    title: string;
    process: string;
    completed: boolean;
}

export const SOPDocumenter: React.FC<SOPDocumenterProps> = ({ client }) => {
    const isEligible = client.currentStage === 'Growth' || client.currentStage === 'Advocacy';
    const [sops, setSops] = useState<SOPEntry[]>([]);
    const [showForm, setShowForm] = useState(false);
    const [newTitle, setNewTitle] = useState('');
    const [newProcess, setNewProcess] = useState('');
    const [status, setStatus] = useState<'idle' | 'saving'>('idle');

    if (!isEligible) return null;

    const handleAdd = () => {
        if (!newTitle.trim() || !newProcess.trim()) return;
        const entry: SOPEntry = {
            id: `sop-${Date.now()}`,
            title: newTitle.trim(),
            process: newProcess.trim(),
            completed: false,
        };
        setSops(prev => [...prev, entry]);
        setNewTitle('');
        setNewProcess('');
        setShowForm(false);
        feedbackBus.emit('activity_completed', {
            activityLabel: `SOP documented: ${entry.title}`,
            clientEmail: client.email,
        });
    };

    const toggleComplete = (id: string) => {
        setSops(prev => prev.map(s => s.id === id ? { ...s, completed: !s.completed } : s));
    };

    const handleDelete = (id: string) => {
        setSops(prev => prev.filter(s => s.id !== id));
    };

    const handleSave = async () => {
        setStatus('saving');
        await new Promise(r => setTimeout(r, 500));
        setStatus('idle');
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
        >
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
                <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-orange-600" />
                    <h3 className="text-sm font-bold text-slate-800">SOP Documenter</h3>
                </div>
                <button
                    onClick={() => setShowForm(!showForm)}
                    className="px-2 py-1 text-[10px] font-medium bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors flex items-center gap-1"
                >
                    <Plus className="w-3 h-3" />
                    Add SOP
                </button>
            </div>
            <div className="p-4 space-y-3">
                {showForm && (
                    <div className="p-3 bg-orange-50 rounded-lg border border-orange-100 space-y-3">
                        <input
                            value={newTitle}
                            onChange={e => setNewTitle(e.target.value)}
                            placeholder="SOP Title (e.g., Client Onboarding Process)"
                            className="w-full px-3 py-2 text-xs border border-orange-200 rounded-lg focus:ring-1 focus:ring-orange-400 outline-none"
                        />
                        <textarea
                            value={newProcess}
                            onChange={e => setNewProcess(e.target.value)}
                            placeholder="Describe the standard operating procedure..."
                            className="w-full px-3 py-2 text-xs border border-orange-200 rounded-lg resize-none h-20 focus:ring-1 focus:ring-orange-400 outline-none"
                        />
                        <div className="flex justify-end gap-2">
                            <button onClick={() => setShowForm(false)} className="px-3 py-1 text-[10px] text-slate-600 hover:text-slate-800">Cancel</button>
                            <button onClick={handleAdd} disabled={!newTitle.trim() || !newProcess.trim()} className="px-3 py-1 text-[10px] font-medium bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50">Save SOP</button>
                        </div>
                    </div>
                )}
                {sops.length === 0 && !showForm && (
                    <p className="text-xs text-slate-400 italic text-center py-4">No SOPs documented yet. Click "Add SOP" to begin.</p>
                )}
                {sops.map(sop => (
                    <div key={sop.id} className={`flex items-start gap-3 p-3 rounded-lg border transition-all ${
                        sop.completed ? 'bg-green-50 border-green-100' : 'bg-white border-slate-100'
                    }`}>
                        <button onClick={() => toggleComplete(sop.id)} className="mt-0.5 shrink-0">
                            {sop.completed
                                ? <CheckCircle className="w-4 h-4 text-green-500" />
                                : <div className="w-4 h-4 rounded-full border-2 border-slate-300" />}
                        </button>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <Edit3 className="w-3 h-3 text-orange-500" />
                                <span className={`text-xs font-semibold ${sop.completed ? 'text-green-700 line-through' : 'text-slate-700'}`}>
                                    {sop.title}
                                </span>
                            </div>
                            <p className={`text-[10px] mt-1 ${sop.completed ? 'text-green-600' : 'text-slate-500'}`}>{sop.process}</p>
                        </div>
                        <button onClick={() => handleDelete(sop.id)} className="p-1 text-slate-400 hover:text-red-500 transition-colors shrink-0">
                            <Trash2 className="w-3 h-3" />
                        </button>
                    </div>
                ))}
                {sops.length > 0 && (
                    <div className="flex justify-end pt-2 border-t border-slate-100">
                        <button
                            onClick={handleSave}
                            disabled={status === 'saving'}
                            className="px-3 py-1.5 text-[11px] font-medium bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors disabled:opacity-50 flex items-center gap-1"
                        >
                            {status === 'saving' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                            Save All
                        </button>
                    </div>
                )}
            </div>
        </motion.div>
    );
};
